import type {
  SwitchingBlockRead,
  SwitchingBlockUpdate,
  SwitchingBlockWithSwitchings,
  SwitchingBlock,
} from '@gridcon-energy/schaltungstool-backend'
import useFetchData from '~/composables/common/useFetchData'
import { computed, inject, provide, ref, shallowRef, watch } from 'vue'
import type { InjectionKey } from 'vue'
import { injectBackendClient } from '~/api/backendClients'
import { injectHandleFetchError } from '~/plugins/vue-plugins/fetch-error-handler'
import { DateRangeFilter, filterItemsByDateRange } from '~/composables/useActivityDataFilter'
import { defineStore, storeToRefs } from 'pinia'
import dayjs from 'dayjs/esm'
import { useConfirm } from '~/plugins/vue-plugins/use-dialog'
import { Notify } from '~/lib/notify'
import { useI18n } from 'vue-i18n'
import type { ListItemComponentProps, ListItemExtended } from '~/lib/list-items'
import { sortBy, truncate } from '~/helper'
import type { EarliestLatestDates } from '~/lib/switchings'

// Create the injection key for switching blocks repository
export const SwitchingBlocksRepositorySymbol = Symbol(
  'SwitchingBlocksRepositorySymbol',
) as InjectionKey<SwitchingBlocksRepository>

/**
 * Store for switching blocks data management
 * Setup-Store Syntax because of watcher
 * - https://pinia.vuejs.org/core-concepts/#Setup-Stores
 * - https://github.com/vuejs/pinia/discussions/794
 */
const switchingBlocksStore = defineStore('switching-blocks', () => {
  const switchingBlocks = ref<SwitchingBlockWithSwitchings[]>([])
  // Main
  const switchingBlocksMap = ref(new Map<number, SwitchingBlockRead>()) // switching_block.id -> switching_block

  /**
   * Create relationship maps from SwitchingBlockRead
   */
  watch(switchingBlocks, () => {
    // Main
    const _switchingBlocksMap = new Map<number, SwitchingBlockRead>()

    for (const switchingBlock of switchingBlocks.value) {
      // Main
      _switchingBlocksMap.set(switchingBlock.id, switchingBlock)
    }

    // Update refs
    switchingBlocksMap.value = _switchingBlocksMap
  })

  return {
    switchingBlocks,
    switchingBlocksMap,
  }
})

export function useSwitchingBlocksRepository(activityId: number) {
  const { t } = useI18n()
  const confirm = useConfirm()
  const backend = injectBackendClient()
  const handleFetchError = injectHandleFetchError()
  const store = switchingBlocksStore()
  const { switchingBlocks, switchingBlocksMap } = storeToRefs(store)

  /**
   * Fetch switching blocks data
   */
  const fetch = useFetchData.immediate(
    async () => {
      const response = await backend.switchingBlocks.switchingBlockWithSwitchingsAll(activityId)
      switchingBlocks.value = response.data
      return response.data
    },
    {
      onError: handleFetchError,
    },
  )

  const switchingBlockItems = computed((): ListItemExtended<SwitchingBlockRead>[] => {
    const items = switchingBlocks.value.map((item): ListItemExtended<SwitchingBlockRead> => {
      return {
        title: item.name,
        value: item,
        props: {
          subtitle: truncate(item.description, 50, true),
        } as ListItemComponentProps,
      }
    })
    sortBy(items, 'title')
    return items
  })

  /**
   * Filter switching blocks by date range
   */
  function filterSwitchingBlocksByDateRange(
    switchingBlocksToFilter: SwitchingBlockWithSwitchings[],
    dateRangeFilter: DateRangeFilter,
  ): SwitchingBlockWithSwitchings[] {
    return filterItemsByDateRange(switchingBlocksToFilter, 'end_latest', 'start_earliest', dateRangeFilter)
  }

  /**
   * Get earliest and latest dates from switching blocks
   */
  const earliestLatestDates = ref<EarliestLatestDates | null>(null)

  watch(switchingBlocks, () => {
    if (switchingBlocks.value.length === 0) {
      earliestLatestDates.value = null
      return
    }

    const dates = switchingBlocks.value
      .filter((block) => block.start_earliest && block.end_latest)
      .map((block) => ({
        start: dayjs(block.start_earliest),
        end: dayjs(block.end_latest),
      }))

    if (dates.length > 0) {
      const earliest = dates.reduce(
        (min, current) => (current.start.isBefore(min) ? current.start : min),
        dates[0].start,
      )
      const latest = dates.reduce((max, current) => (current.end.isAfter(max) ? current.end : max), dates[0].end)

      earliestLatestDates.value = { earliest, latest }
    }
  })

  /**
   * Create a new switching block
   */
  async function create(item: SwitchingBlock): Promise<SwitchingBlockWithSwitchings | undefined> {
    try {
      const response = await backend.switchingBlocks.switchingBlocksCreate(item)
      const switchingBlock = response.data
      switchingBlocks.value.unshift(switchingBlock)
      Notify.success(t('CreateSuccessful'))
      return switchingBlock
    } catch (e) {
      handleFetchError(e)
    }
  }

  /**
   * Update an existing switching block
   */
  async function update(item: SwitchingBlockUpdate): Promise<SwitchingBlockRead | null> {
    try {
      const response = await backend.switchingBlocks.switchingBlocksUpdate(item.id, item)
      const block = switchingBlocksMap.value.get(item.id)
      if (block) {
        Object.assign(block, response.data)
      }
      Notify.info('Update functionality not yet implemented in backend')
      return response.data
    } catch (e) {
      handleFetchError(e)
    }
    return null
  }

  /**
   * Delete a switching block
   */
  async function deleteConfirm(item: number | SwitchingBlockRead) {
    const confirmed = await confirm.delete()
    if (!confirmed) {
      return null
    }
    try {
      const id = typeof item === 'number' ? item : item.id
      await backend.switchingBlocks.switchingBlocksDelete(id)
      const index = switchingBlocks.value.findIndex((s) => s.id === id)
      if (index >= 0) {
        switchingBlocks.value.splice(index, 1)
      }
      Notify.info(t('DeleteSuccessful'))
    } catch (e) {
      handleFetchError(e)
    }
  }

  return {
    switchingBlocks,
    switchingBlocksMap,
    switchingBlockItems: switchingBlockItems,
    fetch,
    create,
    update,
    deleteConfirm,
    filterSwitchingBlocksByDateRange,
    earliestLatestDates,
  }
}

export function provideSwitchingBlocksRepository(activityId: number) {
  const repository = useSwitchingBlocksRepository(activityId)
  provide(SwitchingBlocksRepositorySymbol, repository)
  return repository
}

export function injectSwitchingBlocksRepository() {
  const repository = inject(SwitchingBlocksRepositorySymbol)
  if (!repository) {
    throw new Error('SwitchingBlocksRepository not provided')
  }
  return repository
}

export type SwitchingBlocksRepository = ReturnType<typeof useSwitchingBlocksRepository>
